/**
 * @description Componente de seleção de tipo de conta (CPF ou CNPJ)
 * @module AccountTypeSelection
 * @since Dezembro 2024
 */

import {
  Stack,
  Typography,
  Button,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Groups as GroupsIcon,
  AccountCircle as AccountCircleIcon,
  Healing as HealingIcon,
  Pets as PetsIcon,
} from "@mui/icons-material";
import logoHeader from "../../../assets/fluxmed-logo-horizontal.png";
import { currentFeatures, endpoints } from "@/configs/Settings";

interface AccountTypeSelectionProps {
  onSelectType: (type: "cpf" | "cnpj") => void;
}

export function AccountTypeSelection({
  onSelectType,
}: AccountTypeSelectionProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Stack spacing={4} alignItems="center" width="100%">
      {/* Logo */}
      <Box
        sx={{
          textAlign: "center",
          mb: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          src={logoHeader}
          height="auto"
          alt="Logo"
          style={
            currentFeatures.defaultApp
              ? {
                  width: "250px",
                  height: "auto",
                }
              : {
                  height: "150px",

                  transform: "scale(1.6)",
                  margin: "0 auto 16px auto",
                }
          }
        />
      </Box>

      {/* Título e descrição */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          gutterBottom
          sx={{ fontWeight: 600, color: "#1a1a1a" }}
        >
          Como deseja utilizar a plataforma?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Escolha a opção ideal para você
        </Typography>
      </Box>

      {/* Cards de seleção */}
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={3}
        width="100%"
        justifyContent="center"
        alignItems="stretch"
      >
        {/* Card Paciente */}
        <Paper
          elevation={4}
          onClick={() =>
            window.open(`${endpoints.XDSB_domain}/portal#/`, "_blank")
          }
          sx={{
            p: isMobile ? 2.5 : 3.5,
            width: { xs: "100%", lg: "32%" },
            maxWidth: 400,
            cursor: "pointer",
            background:
              "linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(123, 31, 162, 0.03) 100%)",
            border: "2px solid #9C27B0",
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0 8px 24px rgba(156, 39, 176, 0.25)",
              borderColor: "#7B1FA2",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #9C27B0 0%, #7B1FA2 100%)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              height: "100%",
            }}
          >
            {/* Ícone */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              {currentFeatures.defaultApp ? (
                <HealingIcon
                  sx={{
                    fontSize: isMobile ? 50 : 60,
                    color: "#9C27B0",
                  }}
                />
              ) : (
                <PetsIcon
                  sx={{
                    fontSize: isMobile ? 50 : 60,
                    color: "#9C27B0",
                  }}
                />
              )}
            </Box>

            {/* Título */}
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                fontWeight: 700,
                color: "#9C27B0",
                mb: 1,
              }}
            >
              {currentFeatures.defaultApp ? "Sou Paciente" : "Sou Tutor"}
            </Typography>

            {/* Subtítulo */}
            <Typography
              variant={isMobile ? "body2" : "body1"}
              color="text.primary"
              sx={{
                mb: 2.5,
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              {currentFeatures.defaultApp
                ? "Acesse a plataforma para cuidar da sua saúde"
                : "Acesse a plataforma para cuidar do seu pet"}
            </Typography>

            {/* Lista de benefícios */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                mb: 3,
                width: "100%",
                flex: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#9C27B0", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Agende consultas de forma rápida e fácil
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#9C27B0", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  {currentFeatures.defaultApp
                    ? "Acesse seu histórico médico completo"
                    : "Acesse o histórico completo do seu pet"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#9C27B0", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Receba notificações e lembretes importantes
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#9C27B0", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Comunique-se diretamente com profissionais
                </Typography>
              </Box>
            </Box>

            {/* Botão */}
            <Button
              variant="contained"
              size={isMobile ? "medium" : "large"}
              fullWidth
              startIcon={
                currentFeatures.defaultApp ? <HealingIcon /> : <PetsIcon />
              }
              onClick={(e) => {
                e.stopPropagation();
                window.open(`${endpoints.XDSB_domain}/portal#/`, "_blank");
              }}
              sx={{
                bgcolor: "#9C27B0",
                px: isMobile ? 2 : 4,
                py: isMobile ? 1.3 : 1.6,
                fontSize: isMobile ? "0.95rem" : "1.05rem",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(156, 39, 176, 0.3)",
                "&:hover": {
                  bgcolor: "#7B1FA2",
                  boxShadow: "0 6px 16px rgba(156, 39, 176, 0.4)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {currentFeatures.defaultApp
                ? "Acessar como Paciente"
                : "Acessar como Tutor"}
            </Button>
          </Box>
        </Paper>

        {/* Card CPF - Profissional Autônomo */}
        <Paper
          elevation={4}
          onClick={() => onSelectType("cpf")}
          sx={{
            p: isMobile ? 2.5 : 3.5,
            width: { xs: "100%", lg: "32%" },
            maxWidth: 400,
            cursor: "pointer",
            background:
              "linear-gradient(135deg, rgba(0, 168, 132, 0.08) 0%, rgba(0, 140, 110, 0.03) 100%)",
            border: "2px solid #00A884",
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0 8px 24px rgba(0, 168, 132, 0.25)",
              borderColor: "#008c6e",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #00A884 0%, #008c6e 100%)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              height: "100%",
            }}
          >
            {/* Ícone */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <PersonIcon
                sx={{
                  fontSize: isMobile ? 50 : 60,
                  color: "#00A884",
                }}
              />
            </Box>

            {/* Título */}
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                fontWeight: 700,
                color: "#00A884",
                mb: 1,
              }}
            >
              Registro com CPF
            </Typography>

            {/* Subtítulo */}
            <Typography
              variant={isMobile ? "body2" : "body1"}
              color="text.primary"
              sx={{
                mb: 2.5,
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              Acesso gratuito para funcionalidades básicas
            </Typography>

            {/* Lista de benefícios */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                mb: 3,
                width: "100%",
                flex: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#00A884", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Gerencie agendas e realize teleatendimento
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#00A884", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Assinatura digital de documentos integrado
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#00A884", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Receba consulta diretamente em sua conta do MercadoPago
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <AccountCircleIcon
                  sx={{ fontSize: 22, color: "#00A884", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Integre modelos de Inteligência Artificial
                </Typography>
              </Box>
            </Box>

            {/* Botão */}
            <Button
              variant="contained"
              size={isMobile ? "medium" : "large"}
              fullWidth
              startIcon={<PersonIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onSelectType("cpf");
              }}
              sx={{
                bgcolor: "#00A884",
                px: isMobile ? 2 : 4,
                py: isMobile ? 1.3 : 1.6,
                fontSize: isMobile ? "0.95rem" : "1.05rem",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.3)",
                "&:hover": {
                  bgcolor: "#008c6e",
                  boxShadow: "0 6px 16px rgba(0, 168, 132, 0.4)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Registro com CPF
            </Button>
          </Box>
        </Paper>

        {/* Card CNPJ - Clínica/Empresa */}
        <Paper
          elevation={4}
          onClick={() => onSelectType("cnpj")}
          sx={{
            p: isMobile ? 2.5 : 3.5,
            width: { xs: "100%", lg: "32%" },
            maxWidth: 400,
            cursor: "pointer",
            background:
              "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)",
            border: "2px solid #2196F3",
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0 8px 24px rgba(33, 150, 243, 0.25)",
              borderColor: "#1976D2",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #2196F3 0%, #1976D2 100%)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              height: "100%",
            }}
          >
            {/* Ícone */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <BusinessIcon
                sx={{
                  fontSize: isMobile ? 50 : 60,
                  color: "#2196F3",
                }}
              />
            </Box>

            {/* Título */}
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                fontWeight: 700,
                color: "#2196F3",
                mb: 1,
              }}
            >
              Registro com CNPJ
            </Typography>

            {/* Subtítulo */}
            <Typography
              variant={isMobile ? "body2" : "body1"}
              color="text.primary"
              sx={{
                mb: 2.5,
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              Acesso gratuito para funcionalidades básicas ( até 3
              profissionais)
            </Typography>

            {/* Lista de benefícios */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                mb: 3,
                width: "100%",
                flex: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#2196F3", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Todos os recursos do cadastro individual
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <GroupsIcon sx={{ fontSize: 22, color: "#2196F3", mt: 0.2 }} />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left", fontWeight: 600, color: "#2196F3" }}
                >
                  Convide profissionais para trabalhar na sua clínica
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#2196F3", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Gerencie múltiplos profissionais em uma única organização
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <CheckCircleIcon
                  sx={{ fontSize: 22, color: "#2196F3", mt: 0.2 }}
                />
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  sx={{ textAlign: "left" }}
                >
                  Controle de acesso e permissões por profissional
                </Typography>
              </Box>
            </Box>

            {/* Destaque especial */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 2.5,
                bgcolor: "rgba(33, 150, 243, 0.1)",
                border: "1px solid #2196F3",
                borderRadius: 2,
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#1976D2",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                <GroupsIcon sx={{ fontSize: 18 }} />
                Ideal para clínicas com equipe
              </Typography>
            </Paper>

            {/* Botão */}
            <Button
              variant="contained"
              size={isMobile ? "medium" : "large"}
              fullWidth
              startIcon={<BusinessIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onSelectType("cnpj");
              }}
              sx={{
                bgcolor: "#2196F3",
                px: isMobile ? 2 : 4,
                py: isMobile ? 1.3 : 1.6,
                fontSize: isMobile ? "0.95rem" : "1.05rem",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                "&:hover": {
                  bgcolor: "#1976D2",
                  boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Registro com CNPJ
            </Button>
          </Box>
        </Paper>
      </Stack>

      {/* Nota informativa */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          bgcolor: "rgba(0, 0, 0, 0.03)",
          borderRadius: 2,
          maxWidth: 800,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: "center", display: "block" }}
        >
          <strong>Dica:</strong> Se você é paciente, acesse o portal. Se é
          profissional autônomo, escolha CPF. Se possui uma clínica com outros
          profissionais, escolha CNPJ para poder convidá-los.
        </Typography>
      </Box>
    </Stack>
  );
}
